/**
 * Custom Script System for StorySplat Viewer
 *
 * Provides runtime execution of user-defined scripts with access
 * to the StorySplat Viewer API (engine-agnostic).
 *
 * SECURITY MODEL:
 * Scripts are sandboxed with defense-in-depth:
 *  1. Allowlist proxy on `viewer` — only safe methods exposed, `app`/`destroy` blocked
 *  2. Allowlist proxy on `canvas` — DOM traversal (`ownerDocument`, `parentElement`) blocked
 *  3. 50+ dangerous globals shadowed as function parameters (fetch, Worker, eval, etc.)
 *  4. Constructor chain frozen during execution to block [].constructor.constructor('return this')()
 *  5. Strict mode + null `this` binding
 *
 * This is NOT a browser-kernel isolation boundary (that would require a sandboxed iframe).
 * It prevents scripts from accidentally or casually accessing anything outside the viewer API.
 * Scripts can ONLY interact with the scene through the provided `viewer` object.
 *
 * NOTE: new Function() is intentionally used here to execute user-authored custom scripts.
 * Only scene authors can add scripts to their own scenes.
 */
import * as pc from 'playcanvas';
import type { ViewerInstance } from '../types';

// ─── Allowlisted viewer methods ───────────────────────────────────────────────
// Scripts can ONLY call these methods on the viewer object. Everything else
// (especially `app`, `canvas`, `destroy`) is blocked.
const ALLOWED_VIEWER_METHODS = new Set([
    // Navigation
    'goToWaypoint', 'nextWaypoint', 'prevWaypoint',
    'getCurrentWaypointIndex', 'getWaypointCount',
    'goToExtraWaypoint', 'getExtraWaypoints',
    // Camera
    'setCameraMode', 'getCameraMode', 'setExploreMode',
    'setPosition', 'setRotation', 'getPosition', 'getRotation',
    // Playback
    'play', 'pause', 'stop', 'isPlaying',
    'setProgress', 'getProgress',
    // Entity animations
    'playEntityAnimation', 'pauseEntityAnimation',
    'resetEntityAnimation', 'getEntityAnimations',
    'restartEntityAnimation', 'seekEntityAnimation', 'isEntityAnimationPlaying',
    // Audio
    'muteAll', 'unmuteAll', 'isMuted',
    // Hotspots
    'getHotspots', 'triggerHotspot', 'closeHotspot', 'runLinkedAction',
    // Cutaway (with the scene's Cutaway button or an element that cuts the scene)
    'setCutaway', 'getCutaway',
    // Hotspot folders and the visitor directory
    'setHotspotFolderVisible', 'showOnlyHotspotFolder', 'getVisibleHotspotFolders', 'getHotspotFolders',
    'getDirectory', 'openDirectory', 'closeDirectory',
    // Overlays and triggered regions
    'showOverlay', 'hideOverlay', 'getVisibleOverlays',
    'setRegionActive', 'getActiveRegions',
    // Regions (and the annotation-layer names they replaced, still working)
    'setRegionVisible', 'showOnlyRegion', 'getVisibleRegions', 'getRegions',
    'setLayerGroupVisible', 'showOnlyLayerGroup', 'setAllLayerGroupsVisible', 'getVisibleLayerGroups', 'getLayerGroups',
    // Measurement sets
    'getMeasurementSets', 'setMeasurementSetVisible', 'setAllMeasurementSetsVisible',
    // Splat swapping
    'goToOriginalSplat', 'goToSplat', 'getCurrentSplatUrl',
    'isShowingOriginalSplat', 'getAdditionalSplats',
    // 4DGS
    'setFrame', 'getCurrentFrame', 'getTotalFrames',
    'setFps', 'getFps', 'getFrameProgress', 'setFrameProgress',
    // UI
    'setButtonLabels',
    // Events
    'on', 'off',
    // Lifecycle
    'resize',
]);

// ─── Allowlisted canvas properties ────────────────────────────────────────────
const ALLOWED_CANVAS_PROPS = new Set([
    'width', 'height', 'clientWidth', 'clientHeight',
    'getBoundingClientRect',
    'addEventListener', 'removeEventListener',
]);

// ─── DOM traversal properties to block on canvas ──────────────────────────────
const BLOCKED_CANVAS_PROPS = new Set([
    'ownerDocument', 'parentElement', 'parentNode', 'getRootNode',
    'closest', 'querySelector', 'querySelectorAll',
    'children', 'childNodes', 'firstChild', 'lastChild',
    'nextSibling', 'previousSibling', 'nextElementSibling', 'previousElementSibling',
    'innerHTML', 'outerHTML', 'insertAdjacentHTML', 'insertAdjacentElement',
    'append', 'appendChild', 'removeChild', 'replaceChild', 'remove',
    'constructor', '__proto__', 'prototype',
]);

// ─── Globals to shadow ────────────────────────────────────────────────────────
// Every name here becomes a function parameter set to `undefined`, preventing
// the script from accessing the real global.
const BLOCKED_GLOBALS = [
    // DOM / Window
    'window', 'document', 'self', 'globalThis', 'top', 'parent', 'frames', 'opener',
    // Module systems
    'exports', 'module', 'require', 'define',
    // Network
    'fetch', 'XMLHttpRequest', 'WebSocket', 'EventSource',
    // Storage
    'localStorage', 'sessionStorage', 'indexedDB', 'caches',
    // Workers / Eval
    'Worker', 'SharedWorker', 'ServiceWorker', 'Function', 'importScripts',
    // Constructors that enable escapes
    'Blob', 'File', 'FormData', 'URL', 'URLSearchParams',
    'Image', 'Audio', 'MediaStream',
    // Dialogs
    'alert', 'confirm', 'prompt',
    // Location / History
    'location', 'history',
    // Observers
    'MutationObserver', 'IntersectionObserver', 'ResizeObserver',
    // Messaging
    'postMessage', 'BroadcastChannel',
    // Reflection / meta-programming (sandbox escape via Reflect.construct, Proxy traps, Symbol.unscopables)
    'Proxy', 'Reflect', 'Symbol',
    // Misc dangerous
    'close', 'stop', 'getComputedStyle', 'matchMedia',
    'btoa', 'atob', 'structuredClone', 'reportError', 'createImageBitmap',
    'navigator', 'screen', 'crypto',
    'requestIdleCallback',
] as const;

/** Whether a custom script may call this viewer method (the allowlist above). */
export function isScriptViewerMethod(name: string): boolean {
    return ALLOWED_VIEWER_METHODS.has(name);
}

/**
 * Create an allowlist proxy for the viewer — only exposes safe scene-control methods.
 * Blocks `app` (PlayCanvas engine → DOM traversal), `canvas`, `destroy`, `navigateToScene`.
 */
function createSafeViewerProxy(viewer: ViewerInstance): Record<string, unknown> {
    return new Proxy(viewer as unknown as Record<string, unknown>, {
        get(target, prop) {
            if (prop === 'constructor' || prop === '__proto__' || prop === 'prototype') return undefined;
            if (typeof prop === 'string' && ALLOWED_VIEWER_METHODS.has(prop)) {
                const value = (target as Record<string, unknown>)[prop];
                if (typeof value === 'function') return (value as Function).bind(target);
                return value;
            }
            return undefined;
        },
        set() { return false; },
        has(_target, prop) { return typeof prop === 'string' && ALLOWED_VIEWER_METHODS.has(prop); },
        ownKeys() { return [...ALLOWED_VIEWER_METHODS]; },
        getPrototypeOf() { return null; },
    });
}

/**
 * Create an allowlist proxy for the canvas — blocks DOM traversal (ownerDocument etc.)
 */
function createSafeCanvasProxy(canvas: HTMLCanvasElement): Record<string, unknown> {
    return new Proxy(canvas as unknown as Record<string, unknown>, {
        get(target, prop) {
            if (BLOCKED_CANVAS_PROPS.has(prop as string)) return undefined;
            if (typeof prop === 'string' && ALLOWED_CANVAS_PROPS.has(prop)) {
                const value = (target as unknown as Record<string, unknown>)[prop];
                if (typeof value === 'function') return (value as Function).bind(target);
                return value;
            }
            return undefined;
        },
        set() { return false; },
        getPrototypeOf() { return null; },
    });
}

/**
 * Temporarily freeze the constructor chain on built-in prototypes.
 * This blocks the primary sandbox escape: [].constructor.constructor('return this')()
 * Returns a restore function that MUST be called in a finally block.
 */
function freezeConstructorChain(): () => void {
    const originals: Array<[object, PropertyDescriptor | undefined]> = [];

    const targets = [
        Object.prototype, Array.prototype, String.prototype,
        Number.prototype, Boolean.prototype, RegExp.prototype,
        Error.prototype, TypeError.prototype, RangeError.prototype,
        Map.prototype, Set.prototype, WeakMap.prototype, WeakSet.prototype,
        Promise.prototype,
    ];

    // Also cover async/generator function prototypes
    try { targets.push((async () => {}).constructor.prototype); } catch { /* noop */ }
    try { targets.push((function* () {}).constructor.prototype); } catch { /* noop */ }
    try { targets.push((async function* () {}).constructor.prototype); } catch { /* noop */ }

    for (const target of targets) {
        const desc = Object.getOwnPropertyDescriptor(target, 'constructor');
        originals.push([target, desc]);
        Object.defineProperty(target, 'constructor', {
            get() { return undefined; },
            configurable: true,
        });
    }

    return () => {
        for (const [target, desc] of originals) {
            if (desc) {
                Object.defineProperty(target, 'constructor', desc);
            } else {
                delete (target as { constructor?: unknown }).constructor;
            }
        }
    };
}

/**
 * Create a safe console that only exposes logging methods
 */
function createSafeConsole(): Record<string, (...args: unknown[]) => void> {
    const safe = {
        log: console.log.bind(console),
        warn: console.warn.bind(console),
        error: console.error.bind(console),
        info: console.info.bind(console),
        debug: console.debug.bind(console),
    };
    Object.freeze(safe);
    return safe;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * API exposed to custom scripts
 */
export interface CustomScriptAPI {
    /** StorySplat Viewer instance — the primary API for controlling the viewer */
    viewer: ViewerInstance;
    /** Canvas element */
    canvas: HTMLCanvasElement;
    /** Get current scroll/progress percentage (0-1) */
    getScrollPercentage: () => number;
    /** Get current waypoint index */
    getCurrentWaypointIndex: () => number;
    /** Register a cleanup function to be called on script disposal */
    registerCleanup?: (fn: () => void) => void;
}

/**
 * Custom Script System
 *
 * Executes user-provided scripts with access to the StorySplat Viewer API.
 * Scripts are sandboxed — they can only interact with the scene through the
 * allowlisted viewer methods and cannot access the DOM, network, or storage.
 */
export class CustomScriptSystem {
    private customScript: string;
    private api: CustomScriptAPI;
    private isInitialized: boolean = false;
    private scriptCleanup: (() => void)[] = [];
    private lastError: unknown = null;
    private updateCallbacks: (() => void)[] = [];
    private app: pc.Application | null = null;
    private cancelWatchdog: (() => void) | null = null;

    constructor(customScript: string) {
        this.customScript = customScript;
        this.api = {} as CustomScriptAPI;
    }

    /**
     * Initialize the script system with the viewer API
     */
    initialize(api: CustomScriptAPI, app: pc.Application): void {
        this.api = {
            ...api,
            registerCleanup: (fn: () => void) => this.addCleanup(fn)
        };
        this.app = app;
        this.isInitialized = true;
    }

    /**
     * Update the script content and re-execute
     */
    updateScript(script: string): void {
        if (script === this.customScript) return;
        this.customScript = script;
        this.execute();
    }

    /**
     * Add a cleanup function to be called on disposal
     */
    private addCleanup(fn: () => void): void {
        if (typeof fn === 'function') {
            this.scriptCleanup.push(fn);
        }
    }

    /**
     * Sanitize script to remove problematic characters
     */
    private sanitizeScript(script: string): string {
        if (!script) return '';
        let s = script.normalize('NFC');
        s = s.replace(/\uFEFF/g, '');
        s = s.replace(/\u00A0/g, ' ');
        s = s.replace(/[\u2028\u2029]/g, '\n');
        s = s.replace(/[\u200B-\u200D\u2060]/g, '');
        s = s.replace(/[\u2018\u2019\u201B]/g, "'").replace(/[\u201C\u201D\u201E]/g, '"');
        return s;
    }

    /**
     * Preprocess script with safety wrapping
     */
    private preprocessScript(script: string): string {
        if (!script) return '';
        let processed = this.sanitizeScript(script).trim().replace(/\r\n/g, '\n');
        if (/console\/log\s*\(/.test(processed)) {
            console.warn("[Custom Script] Detected 'console/log(...)'. Did you mean 'console.log(...)'?");
            processed = processed.replace(/console\/log\s*\(/g, 'console.log(');
        }
        processed = "try {\n" + processed + "\n} catch (error) {\n  console.error('[Custom Script] Runtime error:', error);\n}";
        return processed;
    }

    /**
     * Execute the custom script in a hardened sandbox
     */
    execute(): void {
        if (!this.isInitialized || !this.customScript || !this.app) {
            return;
        }
        if (this.customScript.length > 200000) {
            console.warn('[Custom Script] Script too large, aborting execution.');
            return;
        }
        this.cleanup();
        const processedScript = this.preprocessScript(this.customScript);

        // Block dynamic import() — it's a language-level feature that can't be shadowed
        if (/\bimport\s*\(/.test(processedScript)) {
            console.warn('[Custom Script] Dynamic import() is not allowed in custom scripts.');
            return;
        }

        try {
            const app = this.app;
            let cancelled = false;
            this.cancelWatchdog = () => { cancelled = true; };

            // Watchdog to prevent runaway update callbacks
            const watchdog = () => {
                if (cancelled) return;
                if (this.updateCallbacks.length > 200) {
                    console.warn('[Custom Script] Too many update callbacks; further callbacks ignored.');
                    cancelled = true;
                } else {
                    requestAnimationFrame(watchdog);
                }
            };
            requestAnimationFrame(watchdog);

            // registerUpdate hooks into the PlayCanvas frame loop
            const registerUpdate = (callback: () => void) => {
                if (typeof callback !== 'function' || cancelled) return;
                const safeCallback = () => {
                    try { callback(); }
                    catch (err) { console.error('[Custom Script] Error in update callback:', err); }
                };
                this.updateCallbacks.push(safeCallback);
                app.on('update', safeCallback);
                this.addCleanup(() => {
                    app.off('update', safeCallback);
                    const idx = this.updateCallbacks.indexOf(safeCallback);
                    if (idx !== -1) this.updateCallbacks.splice(idx, 1);
                });
            };

            // ── Build sandboxed API objects ──────────────────────────────────
            const safeViewer = createSafeViewerProxy(this.api.viewer);
            const safeCanvas = createSafeCanvasProxy(this.api.canvas);
            const safeConsole = createSafeConsole();
            const { getScrollPercentage, getCurrentWaypointIndex } = this.api;

            // ── Build function parameter names ──────────────────────────────
            // Safe API params, then console, then timer functions, then ALL blocked globals
            const paramNames = [
                'viewer', 'canvas',
                'getScrollPercentage', 'getCurrentWaypointIndex',
                'registerUpdate', 'registerCleanup',
                'console',
                // Timer functions — safe to provide (they can't escape the sandbox)
                'setTimeout', 'setInterval', 'clearTimeout', 'clearInterval',
                'requestAnimationFrame', 'cancelAnimationFrame',
                'queueMicrotask',
                // All blocked globals
                ...BLOCKED_GLOBALS,
            ];

            // Shadow `eval` inside the body using an IIFE parameter (can't use var/let/const
            // with the name `eval` in strict mode — that's a SyntaxError)
            const body = "'use strict';\n" + `(function(eval) {\n${processedScript}\n}).call(this, undefined);`;

            // ── Freeze constructor chain to block escape vectors ─────────────
            // eslint-disable-next-line no-new-func
            const restoreConstructors = freezeConstructorChain();

            try {
                const func = new Function(...paramNames, body);

                // Build parameter values array
                const paramValues: unknown[] = [
                    safeViewer, safeCanvas,
                    getScrollPercentage, getCurrentWaypointIndex,
                    registerUpdate,
                    (fn: () => void) => this.addCleanup(fn),
                    safeConsole,
                    // Real timer functions (safe — they just schedule callbacks)
                    setTimeout, setInterval, clearTimeout, clearInterval,
                    requestAnimationFrame, cancelAnimationFrame,
                    queueMicrotask,
                    // All blocked globals → undefined
                    ...BLOCKED_GLOBALS.map(() => undefined),
                ];

                // Call with null `this` (strict mode makes it undefined inside)
                const possibleReturn = func.apply(null, paramValues);

                // Check for returned cleanup function
                if (typeof possibleReturn === 'function') {
                    this.addCleanup(possibleReturn);
                }
            } finally {
                // ALWAYS restore constructor chain, even if script throws
                restoreConstructors();
            }

            console.log('[Custom Script] Executed successfully');
        } catch (error) {
            this.lastError = error;
            console.error('[Custom Script] Execution error:', error);
        }
    }

    /**
     * Run all cleanup functions
     */
    cleanup(): void {
        this.cancelWatchdog?.();
        this.cancelWatchdog = null;
        this.scriptCleanup.forEach(fn => {
            try { fn(); }
            catch (error) { console.error('[Custom Script] Cleanup error:', error); }
        });
        this.scriptCleanup = [];
        this.updateCallbacks = [];
    }

    /**
     * Get the last error that occurred during execution
     */
    getLastError(): unknown {
        return this.lastError;
    }

    /**
     * Dispose of the script system and cleanup
     */
    dispose(): void {
        this.cleanup();
        this.isInitialized = false;
        this.app = null;
    }
}

/**
 * Setup custom script system with the viewer
 */
export function setupCustomScript(
    viewer: ViewerInstance,
    app: pc.Application,
    canvas: HTMLCanvasElement,
    customScript: string,
    getScrollPercentage: () => number,
    getCurrentWaypointIndex: () => number,
): CustomScriptSystem | null {
    if (!customScript || customScript.trim() === '') {
        return null;
    }
    console.log('[Custom Script] Initializing with hardened sandbox...');

    const scriptSystem = new CustomScriptSystem(customScript);
    scriptSystem.initialize({
        viewer,
        canvas,
        getScrollPercentage,
        getCurrentWaypointIndex,
    }, app);

    scriptSystem.execute();
    return scriptSystem;
}
